import { useState, useEffect } from "react";
import {
  Box,
  Title,
  Select,
  TextInput,
  Button,
  Checkbox,
  Stack,
  Group,
  Paper,
  Loader,
  Text,
  Badge,
  ScrollArea,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconSearch,
  IconMaximize,
  IconFileSpreadsheet,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useDisclosure } from "@mantine/hooks";
import { zoomToGeometryExtent } from "../../utilities/MapUtils";
import useTransientStore from "../../data/transcientStore";
import { searchGeometries } from "../../services/GeometrySearchService";
import {
  getStatusByAddressSystem,
  getQualityControlByAddressSystem,
  getQualityControl2ByAddressSystem,
  getForemanByContract,
  getContractsByAddressSystemAndUser,
} from "../../services/MapComponentsService";
import { ContractListItem } from "../../types/UserRole";
import { GeometryStatus, QualityControl } from "../../types/BaseGeometry";
import { GeometrySearchResult } from "../../types/GeometrySearch";
import ObjectForm from "../generic/ObjectForm";
import usePermissionStore from "../../data/permissionStore";
import { usePermissions } from "../../hooks/usePermissions";
import { ForemanDto } from "../../types/ForemanDto";
import { convertGeometryToObjectData } from "../../utilities/GeometryConverters";
// OBSOLETE: GetCentreCoordinates no longer used - replaced by zoomToGeometryExtent
// import { GetCentreCoordinates } from '../../utilities/Utilities';
import { ExportableColumn } from "../../types/ExportConfig";
import { getExportableColumns } from "../../services/ExportConfigService";
import { useTranslation } from "../../hooks/useTranslation";

export default function SearchMenu() {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const geometrySearchFilters = useTransientStore(
    (state) => state.geometrySearchFilters,
  );
  const setGeometrySearchFilters = useTransientStore(
    (state) => state.setGeometrySearchFilters,
  );
  const geometrySearchResults = useTransientStore(
    (state) => state.geometrySearchResults,
  );
  const setGeometrySearchResults = useTransientStore(
    (state) => state.setGeometrySearchResults,
  );
  const geometrySearchLoading = useTransientStore(
    (state) => state.geometrySearchLoading,
  );
  const setGeometrySearchLoading = useTransientStore(
    (state) => state.setGeometrySearchLoading,
  );
  const map = useTransientStore((state) => state.map);
  const setSelectedGeometry = useTransientStore(
    (state) => state.setSelectedGeometry,
  );

  // Get setters for geometry stores
  const setCurrentPolylines = useTransientStore(
    (state) => state.setCurrentPolylines,
  );
  const setCurrentPolypoints = useTransientStore(
    (state) => state.setCurrentPolypoints,
  );
  const setCurrentPolygons = useTransientStore(
    (state) => state.setCurrentPolygons,
  );

  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [geometryStatuses, setGeometryStatuses] = useState<GeometryStatus[]>(
    [],
  );
  const [qualityControls1, setQualityControls1] = useState<QualityControl[]>(
    [],
  );
  const [qualityControls2, setQualityControls2] = useState<QualityControl[]>(
    [],
  );
  const [foremen, setForemen] = useState<ForemanDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // State for export columns from DB
  const [exportColumns, setExportColumns] = useState<ExportableColumn[]>([]);

  // State for ObjectForm modal
  const [selectedResult, setSelectedResult] =
    useState<GeometrySearchResult | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const currentUserId = useTransientStore((state) => state.currentUserId);

  const { checkAccess, isLoaded } = usePermissions();
  const canShowCityObjects = checkAccess("CITY_OBJECT_SHOW");
  const hidePreliminaryStatuses = !checkAccess(
    "CITY_OBJECT_HIDE_PRELIMINARY_STATUS",
  );
  // console.log('Permissions loaded:', isLoaded);
  // console.log('Permission CITY_OBJECT_SHOW:', canShowCityObjects);
  // console.log('Permission CITY_OBJECT_HIDE_PRELIMINARY_STATUS (inverse logic):', hidePreliminaryStatuses);

  // Load translations for search geomentry (pageId = 302)
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation, loading: translationsLoading } = useTranslation({
    pageId: 344, // Adjust pageId based on your page configuration
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });

  const isMissing = (translation: string | null | undefined, key: string) => {
    return !translation || translation === key;
  };

  // Load contracts and lookup data
  useEffect(() => {
    const loadData = async () => {
      if (!selectedAddressSystem?.id || !currentUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [
          contractsData,
          statusesData,
          qc1Data,
          qc2Data,
          exportColumnsData,
        ] = await Promise.all([
          getContractsByAddressSystemAndUser(
            selectedAddressSystem.id,
            currentUserId,
          ) as Promise<ContractListItem[]>,
          getStatusByAddressSystem(selectedAddressSystem.id),
          getQualityControlByAddressSystem(selectedAddressSystem.id),
          getQualityControl2ByAddressSystem(selectedAddressSystem.id),
          getExportableColumns(selectedAddressSystem.id),
        ]);

        setContracts(contractsData);
        // console.log("Contracts Data:", contractsData);
        setGeometryStatuses(statusesData);
        setQualityControls1(qc1Data);
        setQualityControls2(qc2Data);
        setExportColumns(exportColumnsData.columns);

        try {
          const foremanResults = await Promise.all(
            contractsData.map(async (contract: ContractListItem) => {
              // console.log(`Loading foremen for contract ID: ${contract.id}`);
              const foremenForContract = await getForemanByContract(
                contract.id,
              );
              // console.log(`Foremen for contract ID ${contract.id}:`, foremenForContract);
              return foremenForContract as ForemanDto[];
            }),
          );

          const merged = foremanResults.flat();
          const uniqueForemen = Array.from(
            new Map(merged.map((f: ForemanDto) => [f.id, f])).values(),
          );
          setForemen(uniqueForemen);
        } catch (foremenError) {
          console.error("Error loading foremen:", foremenError);
          setForemen([]);
        }
        // console.log('Quality Controls 1:', qc1Data);
        // console.log('Quality Controls 2:', qc2Data);
      } catch (error) {
        console.error("Error loading search data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedAddressSystem, currentUserId]);

  // Filter contracts based on contract status
  const filteredContracts = contracts.filter((contract) => {
    if (geometrySearchFilters.contractStatus === "active") {
      return contract.contractStatusDefaultValue === 1;
    } else {
      return contract.contractStatusDefaultValue !== 1;
    }
  });

  const handleContractCheckboxChange = (
    contractId: number,
    checked: boolean,
  ) => {
    const updatedIds = checked
      ? [...geometrySearchFilters.selectedContractIds, contractId]
      : geometrySearchFilters.selectedContractIds.filter(
          (id) => id !== contractId,
        );

    setGeometrySearchFilters({
      ...geometrySearchFilters,
      selectedContractIds: updatedIds,
    });
  };

  const handleSelectAllContracts = () => {
    const allContractIds = filteredContracts.map((contract) => contract.id);
    setGeometrySearchFilters({
      ...geometrySearchFilters,
      selectedContractIds: allContractIds,
    });
  };

  const handleDeselectAllContracts = () => {
    setGeometrySearchFilters({
      ...geometrySearchFilters,
      selectedContractIds: [],
    });
  };

  const handleSearch = async () => {
    if (!selectedAddressSystem?.id) return;

    // Validate that at least one contract is selected
    if (geometrySearchFilters.selectedContractIds.length === 0) {
      return;
    }

    try {
      setGeometrySearchLoading(true);
      setSearchPerformed(true);
      const searchResponse = await searchGeometries(
        selectedAddressSystem.id,
        geometrySearchFilters,
      );
      const filterOutPreliminary = <T extends { statusDesc?: string | null }>(
        items: T[],
      ) => {
        if (!hidePreliminaryStatuses) return items;
        return items.filter((item) => {
          const statusText = (item.statusDesc || "").trim().toLowerCase();
          return (
            !statusText.includes("prelim") && !statusText.includes("planned")
          );
        });
      };

      const filteredResults = filterOutPreliminary(searchResponse.results);
      const filteredPolylines = filterOutPreliminary(searchResponse.polylines);
      const filteredPoints = filterOutPreliminary(searchResponse.points);
      const filteredPolygons = filterOutPreliminary(searchResponse.polygons);
      //console.log('Filtered Results:', filteredResults);
      // Store unified results for display
      setGeometrySearchResults(filteredResults);

      // Store filtered geometries in their respective stores
      setCurrentPolylines(filteredPolylines);
      setCurrentPolypoints(filteredPoints);
      setCurrentPolygons(filteredPolygons);
    } catch (error) {
      console.error("Error searching geometries:", error);
    } finally {
      setGeometrySearchLoading(false);
    }
  };

  const handleClearFilters = () => {
    setGeometrySearchFilters({
      contractStatus: "active",
      selectedContractIds: [],
      geometryName: "",
      geometryType: "all",
      createdBy: "",
      foremanId: null,
      qualityControl1Id: null,
      qualityControl2Id: null,
      fromDate: null,
      toDate: null,
      geometryStatusId: null,
      sortBy: "name",
    });
    setGeometrySearchResults([]);
    setSearchPerformed(false);

    // Clear geometry stores
    setCurrentPolylines([]);
    setCurrentPolypoints([]);
    setCurrentPolygons([]);
  };

  const handleOpenObjectForm = (result: GeometrySearchResult) => {
    setSelectedResult(result);
    setSelectedGeometry(result.fullGeometry || null); // Update shared state
    open();

    // Apply zoom to extent logic (matching map click behavior)
    zoomToGeometryExtent(map, result.fullGeometry?.wkt);
  };

  const handleCloseObjectForm = () => {
    close();
    setSelectedResult(null);
  };

  const handleGeometryUpdate = (updatedData: any) => {
    // Handle geometry updates if needed
    //console.log('Geometry updated:', updatedData);
  };

  // Mapping from snake_case column keys (from DB) to GeometrySearchResult properties
  const columnKeyToResultProperty: Record<
    string,
    (result: GeometrySearchResult) => string | number | null | undefined
  > = {
    id: (result) => result.id,
    feature_id: (result) => result.featureId,
    name: (result) => result.name,
    type: (result) => result.type,
    contract_id: (result) => result.contractId,
    contract_name: (result) => result.contractName,
    created_by: (result) => result.createdBy || "",
    foreman_id: (result) => result.foremanId || "",
    foreman_name: (result) => result.foremanName || "",
    quality_control_1: (result) => result.qualityControl1 || "",
    quality_control_2: (result) => result.qualityControl2 || "",
    start_date: (result) =>
      result.startDate ? new Date(result.startDate).toLocaleDateString() : "",
    status: (result) => result.statusDesc || "",
    wkt: (result) => result.wkt,
    address_system_id: (result) => result.addressSystemId,
  };

  const prepareExportData = () => {
    // If no export columns from DB, return empty (shouldn't happen as DB auto-initializes)
    if (exportColumns.length === 0) {
      return [];
    }

    return geometrySearchResults.map((result) => {
      const row: Record<string, string | number | null | undefined> = {};

      exportColumns.forEach((col) => {
        const getter = columnKeyToResultProperty[col.columnKey];
        if (getter) {
          row[col.displayName] = getter(result);
        }
      });

      return row;
    });
  };

  const exportToXLSX = () => {
    const data = prepareExportData();
    if (data.length === 0) return;

    const filePrefix =
      getTranslation("LABEL_SEARCH_RESULTS_FILE_PREFIX") ||
      "geometry_search_results";
    const sheetName =
      getTranslation("LABEL_SEARCH_RESULTS_SHEET_NAME") || "Search Results";

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Auto-size columns
    const maxWidth = 50;
    const colWidths = Object.keys(data[0]).map((key) => ({
      wch: Math.min(
        maxWidth,
        Math.max(
          key.length,
          ...data.map((row) => String(row[key as keyof typeof row]).length),
        ),
      ),
    }));
    worksheet["!cols"] = colWidths;

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `${filePrefix}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  if (translationsLoading) {
    return (
      <div>
        {getTranslation("LABEL_SEARCH_LOADING_TRANSLATIONS") ||
          "Loading translations..."}
      </div>
    );
  }

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Loader size="lg" />
      </Box>
    );
  }

  if (!canShowCityObjects) {
    return (
      <div>
        <Title order={6} mb="md" px={16} pt={5} c="blue">
          {getTranslation("LABEL_SEARCH_GEOMETRIES_TITLE") ||
            "Search Geometries"}
        </Title>
        <div style={{ padding: 16, color: "gray" }}>
          {getTranslation("LABEL_SEARCH_NO_PERMISSION") ||
            "You do not have permission to view city objects. Please contact your administrator."}
        </div>
      </div>
    );
  }

  const isSearchDisabled =
    geometrySearchFilters.selectedContractIds.length === 0;
  const allSelected =
    filteredContracts.length > 0 &&
    geometrySearchFilters.selectedContractIds.length ===
      filteredContracts.length;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Title order={6} mb="md" px={16} pt={5} c="blue">
        {getTranslation("LABEL_SEARCH_GEOMETRIES_TITLE") || "Search Geometries"}
      </Title>

      <ScrollArea style={{ flex: 1 }} offsetScrollbars>
      <Paper withBorder p="md" mb="md">
        <Stack gap="md">
          {/* Contract Status */}
          <Select
            label={
              getTranslation("LABEL_SEARCH_CONTRACT_STATUS") ||
              "Contract Status"
            }
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_CONTRACT_STATUS") ||
              "Select contract status"
            }
            value={geometrySearchFilters.contractStatus}
            onChange={(value) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                contractStatus: value as "active" | "archived",
                selectedContractIds: [], // Reset selected contracts when status changes
              })
            }
            data={[
              {
                value: "active",
                label:
                  getTranslation("LABEL_SEARCH_ACTIVE_CONTRACTS") ||
                  "Active Contracts",
              },
              {
                value: "archived",
                label:
                  getTranslation("LABEL_SEARCH_ARCHIVED_CONTRACTS") ||
                  "Archived Contracts",
              },
            ]}
          />

          {/* Available Contracts Checklist */}
          <Paper withBorder p="sm">
            <Group justify="space-between" mb="xs">
              <Text fw={500} size="sm">
                {getTranslation("LABEL_AVAILABLE_CONTRACTS") ||
                  "Available Contracts"}{" "}
                <Text component="span" c="red">
                  *
                </Text>
              </Text>
              {filteredContracts.length > 0 && (
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={handleSelectAllContracts}
                    disabled={allSelected}
                  >
                    {getTranslation("LABEL_SEARCH_SELECT_ALL") || "Select All"}
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={handleDeselectAllContracts}
                    disabled={
                      geometrySearchFilters.selectedContractIds.length === 0
                    }
                  >
                    {getTranslation("LABEL_SEARCH_DESELECT_ALL") ||
                      "Deselect All"}
                  </Button>
                </Group>
              )}
            </Group>
            <ScrollArea h={150}>
              <Stack gap="xs">
                {filteredContracts.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    {getTranslation("LABEL_NO_CONTRACTS_AVAILABLE") ||
                      "No contracts available"}
                  </Text>
                ) : (
                  filteredContracts.map((contract) => (
                    <Checkbox
                      key={contract.id}
                      label={contract.name}
                      checked={geometrySearchFilters.selectedContractIds.includes(
                        contract.id,
                      )}
                      onChange={(e) =>
                        handleContractCheckboxChange(
                          contract.id,
                          e.currentTarget.checked,
                        )
                      }
                    />
                  ))
                )}
              </Stack>
            </ScrollArea>
            {/* {isSearchDisabled && (
            <Text size="xs" c="red" mt="xs">
              Please select at least one contract
            </Text>
          )} */}
          </Paper>

          {/* Geometry Name */}
          <TextInput
            label={
              getTranslation("LABEL_SEARCH_GEOMETRY_NAME") || "Geometry Name"
            }
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_GEOMETRY_NAME") ||
              "Enter geometry name"
            }
            value={geometrySearchFilters.geometryName}
            onChange={(e) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                geometryName: e.currentTarget.value,
              })
            }
          />

          {/* Geometry Type */}
          <Select
            label={
              getTranslation("LABEL_SEARCH_GEOMETRY_TYPE") || "Geometry Type"
            }
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_GEOMETRY_TYPE") ||
              "Select geometry type"
            }
            value={geometrySearchFilters.geometryType}
            onChange={(value) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                geometryType: value as "all" | "polygon" | "polyline" | "point",
              })
            }
            data={[
              {
                value: "all",
                label: getTranslation("LABEL_SEARCH_ALL_TYPES") || "All Types",
              },
              {
                value: "polygon",
                label: getTranslation("LABEL_SEARCH_TYPE_POLYGON") || "Polygon",
              },
              {
                value: "polyline",
                label:
                  getTranslation("LABEL_SEARCH_TYPE_POLYLINE") || "Polyline",
              },
              {
                value: "point",
                label: getTranslation("LABEL_SEARCH_TYPE_POINT") || "Point",
              },
            ]}
          />

          {/* Created By */}
          {/* Commented out so that we can refer to it later, if we implement user dropdown */}
          {/* <TextInput
          label="Created By"
          placeholder="Enter creator name"
          value={geometrySearchFilters.createdBy}
          onChange={(e) => setGeometrySearchFilters({
            ...geometrySearchFilters,
            createdBy: e.currentTarget.value
          })}
        /> */}

          {/* Foreman */}
          <Select
            label={getTranslation("LABEL_SEARCH_FOREMAN") || "Foreman"}
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_FOREMAN") || "Select foreman"
            }
            searchable
            clearable
            data={foremen.map((f) => ({
              value: f.id.toString(),
              label: f.name,
            }))}
            value={
              geometrySearchFilters.foremanId
                ? geometrySearchFilters.foremanId.toString()
                : null
            }
            onChange={(value) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                foremanId: value ? parseInt(value, 10) : null,
              })
            }
            nothingFoundMessage={
              getTranslation("LABEL_SEARCH_NO_FOREMEN") ||
              "No foremen available"
            }
          />

          {/* Quality Control 1 */}
          <Select
            label={
              getTranslation("LABEL_SEARCH_QUALITY_CONTROL_1") ||
              "Quality Control 1"
            }
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_QUALITY_CONTROL_1") ||
              "Select quality control 1"
            }
            value={geometrySearchFilters.qualityControl1Id?.toString() || null}
            onChange={(value) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                qualityControl1Id: value ? parseInt(value) : null,
              })
            }
            data={qualityControls1.map((qc) => ({
              value: qc.id.toString(),
              label: qc.description,
            }))}
            clearable
          />

          {/* Quality Control 2 */}
          <Select
            label={
              getTranslation("LABEL_SEARCH_QUALITY_CONTROL_2") ||
              "Quality Control 2"
            }
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_QUALITY_CONTROL_2") ||
              "Select quality control 2"
            }
            value={geometrySearchFilters.qualityControl2Id?.toString() || null}
            onChange={(value) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                qualityControl2Id: value ? parseInt(value) : null,
              })
            }
            data={qualityControls2.map((qc) => ({
              value: qc.id.toString(),
              label: qc.description,
            }))}
            clearable
          />

          {/* Date Range */}
          <Group grow>
            <DatePickerInput
              label={getTranslation("LABEL_SEARCH_FROM_DATE") || "From Date"}
              placeholder={
                getTranslation("PLACEHOLDER_SEARCH_FROM_DATE") ||
                "Select start date"
              }
              value={geometrySearchFilters.fromDate}
              onChange={(value) =>
                setGeometrySearchFilters({
                  ...geometrySearchFilters,
                  fromDate: value,
                })
              }
              clearable
              valueFormat="DD.MM.YYYY"
              styles={{
                label: {
                  fontSize: isMissing(
                    getTranslation("LABEL_SEARCH_FROM_DATE"),
                    "LABEL_SEARCH_FROM_DATE",
                  )
                    ? "10px"
                    : undefined,
                },
                input: {
                  fontSize: isMissing(
                    getTranslation("PLACEHOLDER_SEARCH_FROM_DATE"),
                    "PLACEHOLDER_SEARCH_FROM_DATE",
                  )
                    ? "10px"
                    : undefined,
                },
              }}
            />
            <DatePickerInput
              label={getTranslation("LABEL_SEARCH_TO_DATE") || "To Date"}
              placeholder={
                getTranslation("PLACEHOLDER_SEARCH_TO_DATE") ||
                "Select end date"
              }
              value={geometrySearchFilters.toDate}
              onChange={(value) =>
                setGeometrySearchFilters({
                  ...geometrySearchFilters,
                  toDate: value,
                })
              }
              clearable
              valueFormat="DD.MM.YYYY"
              styles={{
                label: {
                  fontSize: isMissing(
                    getTranslation("LABEL_SEARCH_TO_DATE"),
                    "LABEL_SEARCH_TO_DATE",
                  )
                    ? "10px"
                    : undefined,
                },
                input: {
                  fontSize: isMissing(
                    getTranslation("PLACEHOLDER_SEARCH_TO_DATE"),
                    "PLACEHOLDER_SEARCH_TO_DATE",
                  )
                    ? "10px"
                    : undefined,
                },
              }}
            />
          </Group>

          {/* Geometry Status */}
          <Select
            label={
              getTranslation("LABEL_SEARCH_GEOMETRY_STATUS") ||
              "Geometry Status"
            }
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_GEOMETRY_STATUS") ||
              "Select geometry status"
            }
            value={geometrySearchFilters.geometryStatusId?.toString() || null}
            onChange={(value) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                geometryStatusId: value ? parseInt(value) : null,
              })
            }
            data={geometryStatuses.map((status) => ({
              value: status.id.toString(),
              label: status.description,
            }))}
            clearable
          />

          {/* Sort By */}
          <Select
            label={getTranslation("LABEL_SEARCH_SORT_BY") || "Sort By"}
            placeholder={
              getTranslation("PLACEHOLDER_SEARCH_SORT_BY") ||
              "Select sort option"
            }
            value={geometrySearchFilters.sortBy}
            onChange={(value) =>
              setGeometrySearchFilters({
                ...geometrySearchFilters,
                sortBy: value as "name" | "date",
              })
            }
            data={[
              {
                value: "name",
                label: getTranslation("LABEL_SEARCH_SORT_NAME") || "Name",
              },
              {
                value: "date",
                label: getTranslation("LABEL_SEARCH_SORT_DATE") || "Date",
              },
            ]}
          />
        </Stack>
      </Paper>

      {/* Search Results */}
      {geometrySearchResults.length > 0 && (
        <Box mt="xl">
          <Paper withBorder p="md">
            <Group justify="space-between" mb="md">
              <Title order={6} c="blue">
                {getTranslation("LABEL_SEARCH_RESULTS_TITLE") ||
                  "Search Results"}{" "}
                ({geometrySearchResults.length})
              </Title>
              <Button
                variant="light"
                size="xs"
                leftSection={<IconFileSpreadsheet size={14} />}
                onClick={exportToXLSX}
              >
                {getTranslation("LABEL_SEARCH_EXPORT_EXCEL") ||
                  "Export as Excel"}
              </Button>
            </Group>
            <Stack gap="xs">
              {geometrySearchResults.map((result) => (
                <Paper key={`${result.type}-${result.id}`} p="sm" withBorder>
                  <Stack gap={4}>
                    <Group justify="space-between" wrap="nowrap">
                      <Text fw={600} size="md" truncate style={{ flex: 1 }}>
                        {result.name}
                      </Text>
                      <Group gap="xs" wrap="nowrap">
                        <Badge
                          size="sm"
                          color={
                            result.type === "polygon"
                              ? "blue"
                              : result.type === "polyline"
                                ? "green"
                                : "orange"
                          }
                        >
                          {result.type}
                        </Badge>
                        <Tooltip
                          label={
                            getTranslation("LABEL_SEARCH_OPEN_DETAILS") ||
                            "Open details"
                          }
                        >
                          <ActionIcon
                            variant="light"
                            color="blue"
                            size="sm"
                            onClick={() => handleOpenObjectForm(result)}
                          >
                            <IconMaximize size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                    <Text size="xs" truncate>
                      <b>
                        {getTranslation("LABEL_SEARCH_CONTRACT") || "Contract:"}
                      </b>{" "}
                      {result.contractName}
                    </Text>
                    {result.foremanName && (
                      <Text size="xs">
                        <b>
                          {getTranslation("LABEL_SEARCH_FOREMAN_LABEL") ||
                            "Foreman:"}
                        </b>{" "}
                        {result.foremanName}
                      </Text>
                    )}
                    <Group gap="xs" wrap="nowrap">
                      {result.startDate && (
                        <Text size="xs">
                          <b>
                            {getTranslation("LABEL_SEARCH_DATE") || "Date:"}
                          </b>{" "}
                          {new Date(result.startDate).toLocaleDateString()}
                        </Text>
                      )}
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      {result.statusDesc && (
                        <Text size="xs">
                          <b>
                            {getTranslation("LABEL_SEARCH_STATUS") || "Status:"}
                          </b>{" "}
                          {result.statusDesc}
                        </Text>
                      )}
                    </Group>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* No Results Message */}
      {searchPerformed &&
        !geometrySearchLoading &&
        geometrySearchResults.length === 0 && (
          <Box mt="xl">
            <Text c="dimmed" ta="center">
              {getTranslation("LABEL_SEARCH_NO_RESULTS") ||
                "No geometries found matching your search criteria."}
            </Text>
          </Box>
        )}

      </ScrollArea>

      {/* Action Buttons - Fixed at bottom */}
      <Box style={{ flexShrink: 0, paddingTop: 8, borderTop: '1px solid var(--mantine-color-gray-3)' }}>
      <Group grow pb="md">
        <Tooltip
          label={
            getTranslation("LABEL_SEARCH_NO_CONTRACT_SELECTED") ||
            "No contract selected"
          }
          disabled={!isSearchDisabled}
        >
          <Button
            leftSection={<IconSearch size={16} />}
            onClick={handleSearch}
            loading={geometrySearchLoading}
            disabled={isSearchDisabled}
            size="md"
            style={{
              fontSize: isMissing(
                getTranslation("LABEL_SEARCH_ACTION"),
                "LABEL_SEARCH_ACTION",
              )
                ? "10px"
                : undefined,
            }}
          >
            {getTranslation("LABEL_SEARCH_ACTION") || "Search"}
          </Button>
        </Tooltip>
        <Button
          variant="light"
          onClick={handleClearFilters}
          size="md"
          style={{
            fontSize: isMissing(
              getTranslation("LABEL_SEARCH_CLEAR_FILTERS"),
              "LABEL_SEARCH_CLEAR_FILTERS",
            )
              ? "10px"
              : undefined,
          }}
        >
          {getTranslation("LABEL_SEARCH_CLEAR_FILTERS") || "Clear Filters"}
        </Button>
      </Group>
      </Box>

      {/* ObjectForm Modal */}
      {selectedResult && (
        <ObjectForm
          opened={opened}
          onClose={handleCloseObjectForm}
          objectData={convertGeometryToObjectData(selectedResult.fullGeometry)}
          onSubmit={handleGeometryUpdate}
          addressSystemId={selectedAddressSystem?.id}
          userId={currentUserId}
        />
      )}
    </Box>
  );
}
